import React from "react";
import styles from "./RentPages.module.scss";
import AboutAndContactForm from "../../components/AboutAndContactForm/AboutAndContactForm";
import AreaGuideTeaser from "src/components/AreaGuide/AreaGuideTeaser";
import { rentingProcess } from "src/data/general";
import { backgroundImg } from "src/assets/assetsExport";
import { Link, useNavigate } from "react-router-dom";
import PropertymarkBanner from "../../components/PropertymarkBanner/PropertymarkBanner";

const priceOptions = [
  null,
  1000,
  1500,
  2000,
  2500,
  3000,
  4000,
  5000,
  7500,
  10000,
];
const bedroomOptions = [null, 1, 2, 3, 4, 5];
const bathroomOptions = [null, 1, 2, 3, 4];

const RentPage: React.FC = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = React.useState<string>("");
  const [minPrice, setMinPrice] = React.useState<string>("");
  const [maxPrice, setMaxPrice] = React.useState<string>("");
  const [bedrooms, setBedrooms] = React.useState<string>("");
  const [bathrooms, setBathrooms] = React.useState<string>("");

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (keyword.trim()) params.set("keyword", keyword.trim());
    if (minPrice) params.set("minPrice", minPrice);
    if (maxPrice) params.set("maxPrice", maxPrice);
    if (bedrooms) params.set("bedrooms", bedrooms);
    if (bathrooms) params.set("bathrooms", bathrooms);
    navigate(`/services/rent/properties/list?${params.toString()}`);
  };

  return (
    <>
      {/* HERO */}
      <section className="min-h-[90vh] pt-40 pb-20 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.livingRoom2}
          alt="social events"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
        />
        <div className="absolute inset-0 bg-black/30 z-0"></div>

        <div className="relative z-10 text-center max-w-4xl px-6">
          <h1 className="font-teko leading-none mb-8 tracking-[-2px] text-6xl">
            <span className="stroke-1 text-white">Rent with Us</span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-white max-w-2xl mx-auto mb-12">
            Access London’s most exclusive rental portfolio. Seamless
            application, transparent fees, and a dedicated tenant liaison team
            to support every step.
          </p>
          {/* Search Widget */}
          <div className="mt-12 max-w-4xl mx-auto w-full">
            <div className="bg-[rgba(255,255,255,1)] backdrop-blur-xl border border-solid border-[rgba(0,0,0,0.06)] shadow-[0_30px_80px_rgba(0,0,0,0.08)] p-8">
              <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between gap-4 border-b border-b-solid border-b-[rgba(0,0,0,0.08)] pb-4">
                  <div>
                    <p className="font-manrope text-xs uppercase text-start tracking-[0.2em] text-text-slate mb-1">
                      Quick Search
                    </p>
                    <h3 className="font-teko text-3xl">Find Your Next Home</h3>
                  </div>
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] text-brand-gold hidden sm:block">
                    Filters
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 items-end gap-4">
                  <div className="flex flex-col sm:col-span-2 lg:col-span-1">
                    <label
                      htmlFor="keyword"
                      className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate mb-1 pl-1"
                    >
                      Postcode or Area
                    </label>
                    <input
                      id="keyword"
                      type="text"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      placeholder="e.g. SW3, Kensington"
                      className="w-full px-4 py-3 bg-white/70 border border-solid border-[rgba(0,0,0,0.08)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/40 transition-all duration-200"
                      aria-label="Postcode or area search"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor="minPrice"
                      className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate mb-1 pl-1"
                    >
                      Min Price (PCM)
                    </label>
                    <select
                      id="minPrice"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="w-full px-4 py-3 bg-white/70 border border-solid border-[rgba(0,0,0,0.08)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/40 transition-all duration-200"
                      aria-label="Min budget (PCM)"
                    >
                      {priceOptions.map((p) => (
                        <option key={`min-${p ?? "any"}`} value={p ?? ""}>
                          {p ? `£${p.toLocaleString()}` : "Any"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor="maxPrice"
                      className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate mb-1 pl-1"
                    >
                      Max Price (PCM)
                    </label>
                    <select
                      id="maxPrice"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="w-full px-4 py-3 bg-white/70 border border-solid border-[rgba(0,0,0,0.08)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/40 transition-all duration-200"
                      aria-label="Max budget (PCM)"
                    >
                      {priceOptions.map((p) => (
                        <option key={`max-${p ?? "any"}`} value={p ?? ""}>
                          {p ? `£${p.toLocaleString()}` : "Any"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor="bedrooms"
                      className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate mb-1 pl-1"
                    >
                      Bedrooms (min)
                    </label>
                    <select
                      id="bedrooms"
                      value={bedrooms}
                      onChange={(e) => setBedrooms(e.target.value)}
                      className="w-full px-4 py-3 bg-white/70 border border-solid border-[rgba(0,0,0,0.08)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/40 transition-all duration-200"
                      aria-label="Bedrooms (min)"
                    >
                      {bedroomOptions.map((b) => (
                        <option key={`bed-${b ?? "any"}`} value={b ?? ""}>
                          {b ? `${b}+` : "Any"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor="bathrooms"
                      className="font-manrope text-xs uppercase tracking-[.15em] text-text-slate mb-1 pl-1"
                    >
                      Bathrooms (min)
                    </label>
                    <select
                      id="bathrooms"
                      value={bathrooms}
                      onChange={(e) => setBathrooms(e.target.value)}
                      className="w-full px-4 py-3 bg-white/70 border border-solid border-[rgba(0,0,0,0.08)] font-manrope text-base text-text-obsidian focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/40 transition-all duration-200"
                      aria-label="Bathrooms (min)"
                    >
                      {bathroomOptions.map((b) => (
                        <option key={`bath-${b ?? "any"}`} value={b ?? ""}>
                          {b ? `${b}+` : "Any"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    className="px-6 py-3 bg-text-obsidian text-white font-teko text-xl uppercase tracking-wide hover:bg-brand-yellow hover:text-black transition-all duration-200"
                    onClick={handleSearch}
                  >
                    Search Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PropertymarkBanner />

      {/* 1. RENTING PROCESS (Timeline) */}
      <section className="py-32 relative bg-bg-white">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-20 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              The Journey
            </span>
            <h2 className="page-section-heading">Rent Sequence</h2>
          </div>
          <div
            className={`${styles["timeline-track"]} flex justify-between relative mt-16 before:content-[''] before:absolute before:top-0 before:left-0 before:w-px before:h-full before:bg-[rgba(0,0,0,0.1)] flex-col md:flex-row gap-12 md:before:w-full md:before:h-px md:before:left-0 md:before:top-1`}
          >
            {rentingProcess.map((i, index) => {
              return (
                <div className="flex-1 relative transition-all duration-400 hover:translate-y-[-10px] group md:pt-0 pr-0 md:pr-8  pl-8 md:pl-0">
                  <span className="absolute top-[-4px] left-0 w-2.5 h-2.5 bg-bg-white border border-solid border-text-obsidian transform rotate-45 transition-all duration-400 group-hover:bg-brand-yellow group-hover:border-brand-yellow group-hover:scale-150 sm:top-0 sm:left-[-4px]"></span>
                  <span className="font-teko text-lg text-brand-gold mb-2 block md:mt-10">
                    0{index + 1}
                  </span>
                  <h3 className="font-teko text-2xl mb-4">{i.header}</h3>
                  <p className="font-manrope text-sm text-text-slate leading-relaxed">
                    {i.p}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2. WHY US */}
      <section className="py-32 relative bg-bg-porcelain">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-20 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Advantages
            </span>
            <h2 className="page-section-heading">The Standard</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] transform transition-all duration-300 ease-lux cursor-default relative hover:bg-text-obsidian hover:text-white hover:translate-x-[-20px] hover:shadow-[20px_20px_0px_rgba(0,0,0,0.1)] group lg:transform-none">
              <h3 className="font-teko text-3xl mb-4">Seamless Move-In</h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed">
                Digital paperwork, fast referencing, and agent-led check-in
                ensure your move is stress-free.
              </p>
            </div>
            <div className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] transform transition-all duration-300 ease-lux cursor-default relative hover:bg-text-obsidian hover:text-white hover:translate-x-[-20px] hover:shadow-[20px_20px_0px_rgba(0,0,0,0.1)] group lg:transform-none">
              <h3 className="font-teko text-3xl mb-4">Flexible Contracts</h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed">
                Enjoy short or long let options—choose a tenancy that works for
                your timeline.
              </p>
            </div>
            <div className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] transform transition-all duration-300 ease-lux cursor-default relative hover:bg-text-obsidian hover:text-white hover:translate-x-[-20px] hover:shadow-[20px_20px_0px_rgba(0,0,0,0.1)] group lg:transform-none">
              <h3 className="font-teko text-3xl mb-4">24/7 Support</h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed">
                Our automated maintenance engine logs issues instantly and
                dispatches contractors.
              </p>
            </div>
            <div className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] transform transition-all duration-300 ease-lux cursor-default relative hover:bg-text-obsidian hover:text-white hover:translate-x-[-20px] hover:shadow-[20px_20px_0px_rgba(0,0,0,0.1)] group lg:transform-none">
              <h3 className="font-teko text-3xl mb-4">Furnished Options</h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed">
                Choose from fully, part, or unfurnished homes to suit your
                lifestyle—just let us know your preference.
              </p>
            </div>
          </div>
        </div>
      </section>
<TenantGuides />
      <AreaGuideTeaser />
      <AboutAndContactForm />
    </>
  );
};

export function TenantGuides() {
  return (
    <section className="py-32 relative bg-bg-white">
      <div className="max-w-[1440px] mx-auto px-[5%]">
        <div className="mb-20 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
          <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
            Resources
          </span>
          <h2 className="page-section-heading">Tenant Intelligence</h2>
        </div>
        <div
          className={`${styles["guide-scroller"]} flex gap-12 pb-8 overflow-x-auto`}
        >
          <div className="min-w-[280px] bg-bg-porcelain border border-solid border-[#eee] p-8 px-6 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft hover:bg-white group">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
            <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
              Guide
            </span>
            <h3 className="font-teko text-xl mb-6 leading-tight">
              How to rent
            </h3>
            <Link
              to="/guides/tenants"
              className="font-teko text-lg uppercase border-b border-b-solid border-b-[#ddd] group-hover:text-brand-gold group-hover:border-brand-gold transition-all duration-400"
            >
              Read guide
            </Link>
          </div>
          <div className="min-w-[280px] bg-bg-porcelain border border-solid border-[#eee] p-8 px-6 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft hover:bg-white group">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
            <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
              Finance
            </span>
            <h3 className="font-teko text-xl mb-6 leading-tight">
              How council tax works
            </h3>
            <a
              href="https://www.gov.uk/council-tax/who-has-to-pay"
              target="_blank"
              rel="noopener noreferrer"
              className="font-teko text-lg uppercase border-b border-b-solid border-b-[#ddd] group-hover:text-brand-gold group-hover:border-brand-gold transition-all duration-400"
            >
              Open Link
            </a>
          </div>
          <div className="min-w-[280px] bg-bg-porcelain border border-solid border-[#eee] p-8 px-6 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft hover:bg-white group">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
            <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
              Legal
            </span>
            <h3 className="font-teko text-xl mb-6 leading-tight">
              Right to Rent
            </h3>
            <Link
              to="/guides/tenants#right-to-rent"
              className="font-teko text-lg uppercase border-b border-b-solid border-b-[#ddd] group-hover:text-brand-gold group-hover:border-brand-gold transition-all duration-400"
            >
              Read guide
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default RentPage;
